import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, PermissionCheck } from "@/components";
import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";
import { PermissionRoles } from "@/enum";
import { classNames } from "@/utils/classNames";

import {
	useGetRepairCompaniesQuery,
	useGetRepairFixesQuery,
	useGetRepairListsQuery,
	useGetRepairRunsQuery,
	useRunRepairFixMutation,
} from "../../redux/adminFixes/adminFixes.api";
import { RunsTable } from "./components/RunsTable";

/** While anything is queued or in flight, poll so the table finishes the run in front of you. */
const ACTIVE_POLL_MS = 5000;

/** The title of the item with this id, or "" — which is what tells `AutoComplete` to show search. */
const titleOf = (items: AutoCompleteItem[], id: number | string | null): string =>
	items.find((item) => item.id === id)?.title ?? "";

export const AdminFixes: FunctionComponent = () => {
	const { t } = useTranslation();

	const [companyId, setCompanyId] = useState<number | null>(null);
	const [listId, setListId] = useState<number | null>(null);
	const [fixCode, setFixCode] = useState<string | null>(null);
	const [dryRun, setDryRun] = useState(true);
	const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

	const { data: fixes = [] } = useGetRepairFixesQuery();
	const { data: companies = [] } = useGetRepairCompaniesQuery();
	const { data: lists = [], isFetching: listsLoading } = useGetRepairListsQuery(
		{ companyId: companyId as number },
		{ skip: !companyId }
	);

	const { data: runs = [] } = useGetRepairRunsQuery(undefined, {
		pollingInterval: ACTIVE_POLL_MS,
	});

	const [runFix, { isLoading: isQueueing }] = useRunRepairFixMutation();

	// The id is carried in the option, so the label is free to be as long as it needs to be — these
	// pickers hold thousands of companies and the number is how an operator tells duplicates apart.
	const companyItems: AutoCompleteItem[] = useMemo(
		() =>
			companies.map((company) => ({ id: company.id, title: `${company.name} (#${company.id})` })),
		[companies]
	);

	const listItems: AutoCompleteItem[] = useMemo(
		() => lists.map((list) => ({ id: list.id, title: `#${list.id} — ${list.name}` })),
		[lists]
	);

	const fixItems: AutoCompleteItem[] = useMemo(
		() => fixes.map((fix) => ({ id: fix.code, title: fix.name })),
		[fixes]
	);

	const selectedFix = useMemo(
		() => fixes.find((fix) => fix.code === fixCode) ?? null,
		[fixes, fixCode]
	);

	// An instance-wide fix has no tenant to pick, so the page must neither ask for one nor gate the
	// run on it (PREC-3900); a company-scoped fix names the tenant but has no list (PREC-4191).
	// Defaults to needing both, so an unrecognised scope stays conservative.
	const needsTarget = selectedFix?.scope !== "none";
	const needsList = needsTarget && selectedFix?.scope !== "company";

	const canRun =
		!!fixCode && !isQueueing && (!needsTarget || !!companyId) && (!needsList || !!listId);

	const onCompanySelect = (item?: AutoCompleteItem) => {
		setCompanyId(item ? Number(item.id) : null);
		// The list picker is scoped to the company, so a stale pick would point at another tenant.
		setListId(null);
		setMessage(null);
	};

	const onRun = async () => {
		if (!canRun) {
			return;
		}

		const result = await runFix({
			fixCode: fixCode as string,
			// Omitted entirely for an instance-wide fix — the API rejects a company/list it has no
			// use for, and there is nothing sensible to send. A company-scoped fix sends no list.
			...(needsTarget ? { companyId: companyId as number } : {}),
			...(needsList ? { listId: listId as number } : {}),
			dryRun,
		}).unwrap();

		setMessage(
			result.queued
				? { ok: true, text: t("adminFixes.queued") }
				: { ok: false, text: result.reason ?? t("adminFixes.notQueued") }
		);
	};

	return (
		<PermissionCheck requiredPermissions={[PermissionRoles.GLOBAL_ADMIN]}>
			<div className="w-full">
				<div className="mb-6">
					<h3 className="mb-3">{t("adminFixes.title")}</h3>
					<p className="pr-5 text-xs text-gray-700 lg:max-w-[650px]">{t("adminFixes.subtitle")}</p>
				</div>

				<div className="mb-8 max-w-[720px] rounded-lg border border-gray-200 p-4">
					{!needsTarget && (
						<p className="mb-4 text-xs text-gray-600" data-testid="admin-fix-instance-wide">
							{t("adminFixes.instanceWide")}
						</p>
					)}

					{needsTarget && !needsList && (
						<p className="mb-4 text-xs text-gray-600" data-testid="admin-fix-company-scoped">
							{t("adminFixes.companyScoped")}
						</p>
					)}

					<div className={classNames("grid gap-4 sm:grid-cols-2", !needsTarget && "hidden")}>
						<AutoComplete
							data={companyItems}
							handleSearch={() => setMessage(null)}
							handleSelect={onCompanySelect}
							label={t("adminFixes.company")}
							placeholder={t("adminFixes.searchCompany")}
							selectedItem={titleOf(companyItems, companyId)}
							testId="admin-fix-company"
						/>

						<div className={classNames(!needsList && "hidden")}>
							<AutoComplete
								data={companyId && needsList ? listItems : []}
								handleSearch={() => setMessage(null)}
								handleSelect={(item?: AutoCompleteItem) => setListId(item ? Number(item.id) : null)}
								isLoading={listsLoading}
								isLoadingText={t("adminFixes.loadingLists")}
								label={t("adminFixes.list")}
								placeholder={
									companyId ? t("adminFixes.searchList") : t("adminFixes.chooseCompanyFirst")
								}
								selectedItem={titleOf(listItems, listId)}
								testId="admin-fix-list"
							/>
						</div>
					</div>

					<div className="mt-4">
						<AutoComplete
							data={fixItems}
							handleSearch={() => setMessage(null)}
							handleSelect={(item?: AutoCompleteItem) => setFixCode(item ? String(item.id) : null)}
							label={t("adminFixes.fix")}
							placeholder={t("adminFixes.searchFix")}
							selectedItem={titleOf(fixItems, fixCode)}
							testId="admin-fix-code"
						/>

						{selectedFix && <p className="mt-2 text-xs text-gray-600">{selectedFix.description}</p>}
					</div>

					<div className="mt-4 flex items-center gap-3">
						<input
							checked={dryRun}
							id="admin-fix-dry-run"
							type="checkbox"
							onChange={(event) => setDryRun(event.target.checked)}
						/>
						<label className="text-xs text-gray-700" htmlFor="admin-fix-dry-run">
							{t("adminFixes.dryRunHint")}
						</label>
					</div>

					<div className="mt-4">
						<Button
							disabled={!canRun}
							isLoading={isQueueing}
							title={t("adminFixes.run")}
							type="button"
							onClick={() => void onRun()}
						/>
					</div>

					{message && (
						<p className={`mt-3 text-xs ${message.ok ? "text-green-700" : "text-red-700"}`}>
							{message.text}
						</p>
					)}
				</div>

				<h4 className="mb-3">{t("adminFixes.runs.title")}</h4>
				<RunsTable fixes={fixes} runs={runs} />
			</div>
		</PermissionCheck>
	);
};
